import { Box, Button, IconButton, InputAdornment, TextField, Typography, BaseTextFieldProps, CircularProgress } from '@mui/material'
import React, { useState, useEffect } from 'react'
import { Logo } from '../assets/images'
import { GoogleIcon, LinkedInsmallIcon, MicrosoftIcon, RightArrow } from '../assets/icons'
import Grid from '@mui/material/Grid2'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Link from '@mui/material/Link';
import { useNavigate } from 'react-router-dom'
import AuthService from '../services/auth.service'
import { SocialProvider } from '../types/auth'
import AccountVerification from '../modals/AccountVerification'
import { config } from '../config'
import ConfirmMfa from '../modals/ConfirmMFA'
import { LeadsquaredService } from '../services/leadsquared/leadsquared.service'

const Login = () => {
    const navigate = useNavigate();
    const [value, setValue] = React.useState(0);
    const [showPassword, setShowPassword] = React.useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
    const [verificationData, setVerificationData] = useState({
        companyId: '',
        companyName: '',
        infLeadSquaredId: ''
    });
    const [confirmMfaDialogOpen, setConfirmMfaDialogOpen] = useState(false);
    const [confirmMfaData, setConfirmMfaData] = useState({
        challengeName: '',
        session: ''
    });

    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });

    const [fieldErrors, setFieldErrors] = useState({
        email: '',
        password: ''
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for the field being changed
        if (fieldErrors[name as keyof typeof fieldErrors]) {
            setFieldErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Reset errors
        setError('');
        setFieldErrors({
            email: '',
            password: ''
        });

        // Validate fields
        let hasErrors = false;
        const newFieldErrors = {
            email: '',
            password: ''
        };

        if (!formData.email) {
            newFieldErrors.email = 'Email is required';
            hasErrors = true;
        }

        if (!formData.password) {
            newFieldErrors.password = 'Password is required';
            hasErrors = true;
        }

        if (hasErrors) {
            setFieldErrors(newFieldErrors);
            return;
        }

        setIsLoading(true);

        try {
            const response = await AuthService.login({
                email: formData.email,
                password: formData.password
            });

            if (response?.data?.ChallengeName) { 
                setConfirmMfaDialogOpen(true);
                setConfirmMfaData({
                    challengeName: response?.data?.ChallengeName || '',
                    session: response?.data?.Session || ''
                });
            } else {
                navigate('/dashboard?redirect=easywebinar');
            }

        } catch (err: any) {
            if (err.message === "UserNotConfirmedException") {
                setVerificationData({
                    companyId: err.companyId || '',
                    companyName: err.companyName || '',
                    infLeadSquaredId: err.infLeadSquaredId || ''
                });
                setVerificationDialogOpen(true);
            } else {
                setError(err.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleSocialSignIn = (provider: SocialProvider) => {
        const authUrl =  config.sso_auth_url;
        
        // Create state parameter with additional metadata
        const stateData = {
            isRegistration: false,
            timestamp: Date.now(),
        };

        const baseParams = {
            client_id: config.sso_client_id,
            response_type: 'code',
            scope: 'email openid profile phone',
            redirect_uri: `${window.location.origin}/auth/sso/callback`,
            state: JSON.stringify(stateData),
            prompt: 'select_account'
        };

        const params = new URLSearchParams({
            ...baseParams,
            identity_provider: provider,
        });

        window.location.href = `${authUrl}?${params.toString()}`;
    };

    const handleGoogleSignIn = () => handleSocialSignIn('Google');
    const handleMicrosoftSignIn = () => handleSocialSignIn('Microsoft');
    const handleLinkedinSignIn = () => handleSocialSignIn('Linkedin');

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleVerificationDialogClose = () => {
        setVerificationDialogOpen(false);
    };

    const handleVerificationDialogProceed = async () => {
        // handleVerificationDialogClose();
        setVerificationDialogOpen(false);
        const  LsId  = await LeadsquaredService.getLeadsByEmail(formData.email);
        await LeadsquaredService.joinActivity({ id: LsId.data, activityEvent: 205, activityValue: "Email Verified" });
        const leadData = {
            "leadId": LsId.data,
            "fields": [
                { "Attribute": "mx_Status","Value": "Active"}
            ]
        }
        await LeadsquaredService.updateLsField(leadData);
        navigate('/login');
    };

    const handleConfirmMfaDialogClose = () => {
        setConfirmMfaDialogOpen(false);
    };

    const handleConfirmMfaDialogProceed = () => {
        handleConfirmMfaDialogClose();
        navigate('/dashboard?redirect=easywebinar');
    };

    return (
        <>
            <Box p={{ xs: "16px 16px 11px", sm: "18px 20px 16px", md: "24px 20px 16px" }} sx={{ background: "#F5F6F8" }} display="flex" justifyContent="space-between" alignItems="center" mt={{ xs: "", sm: "unset" }} position="sticky" top="0" zIndex="2">
            <Box display="flex"><img src={Logo} alt="logo" onClick={() => window.open("https://easywebinar.com/", "_blank")} style={{ cursor: "pointer" }}/></Box>
                <Button
                    variant="text"
                    onClick={() => navigate('/signup')}
                    sx={{
                        textTransform: "capitalize",
                        color: "#111827 !important",
                        fontSize: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 16px)",
                        display: "flex",
                        gap: "8px",
                        "&:hover": {
                            boxShadow: "none!important",
                            borderRadius: "8px",
                            "& svg": {
                                transform: "translateX(2px)",
                                transition: "all 0.3s ease",
                            }
                        }
                    }}
                >
                    Create an Account
                    <RightArrow />
                </Button>
            </Box>

            {/* ------------------------- signin component ------------------------ */}
            <Box p={{ xs: "16px", sm: "20px", xl: "48px 48px 0" }} sx={{ backgroundColor: "#F5F6F8", minHeight: "100vh", backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center" }}>
                <Box p={{ xs: "20px", sm: "40px" }} display="flex" flexDirection="column" justifyContent="center" alignItems="center" boxShadow="0px 4px 8px 0px #0000000D" border="1px solid #E9E9E9" bgcolor="#FFFFFF" maxWidth="576px" margin="auto" borderRadius="24px" marginTop="40px" zIndex={{ sm: "2" }} position={{ md: "relative" }}>
                    <>
                        <Typography
                            color="#333333"
                            fontWeight="700"
                            fontSize={{ xs: "min(calc(18px + 6 * (100vw - 400px) / (1920 - 400)), 24px)", lg: "24px" }}

                            textAlign="center"
                        >
                            Welcome back!
                        </Typography>
                        <Typography variant="body1" color="#666666" mb="8px" mt="8px" fontSize={{ xs: "min(calc(14px + 4 * (100vw - 400px) / (1920 - 400)), 14px)" }} textAlign="center">Log in to your dashboard</Typography>
                    </>

                    {error && (
                        <Typography color="error" mb={2}>
                            {error}
                        </Typography>
                    )}

                    <Box sx={{ width: '100%' }} className="umbrella-signIn-tabs">

                        <Box width="100%" textAlign="center">
                            <Box display="flex" mb="14px" mt="14px" justifyContent="center" flexWrap="wrap" gap="12px">
                                <Box height={{ xs: "38px", sm: "42px" }} minWidth={{ xs: "fit-content", sm: "138px" }} px={{ xs: "12px", sm: "20px" }} borderRadius="2.747px" display="flex" alignItems="center" justifyContent="center" border="1px solid #D0D5DD" sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }} onClick={handleGoogleSignIn}>
                                    <Box mr={{ xs: "8px" }} display="flex"><GoogleIcon /></Box>
                                    <Typography variant="body1" color="#0C141D">Google</Typography>
                                </Box>
                                <Box height={{ xs: "38px", sm: "42px" }} minWidth={{ xs: "fit-content", sm: "138px" }} px={{ xs: "12px", sm: "20px" }} borderRadius="2.747px" display="flex" alignItems="center" justifyContent="center" border="1px solid #D0D5DD" sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }} onClick={handleLinkedinSignIn}>
                                    <Box mr={{ xs: "8px" }} display="flex"><LinkedInsmallIcon /></Box>
                                    <Typography variant="body1" color="#0C141D">Linkedin</Typography>
                                </Box>
                                {/* <Box height={{ xs: "38px", sm: "42px" }} minWidth={{ xs: "fit-content", sm: "138px" }} px={{ xs: "12px", sm: "20px" }} borderRadius="2.747px" display="flex" alignItems="center" justifyContent="center" border="1px solid #D0D5DD" sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f7f7f7" } }} onClick={handleMicrosoftSignIn}>
                                    <Box mr={{ xs: "8px" }}><MicrosoftIcon /></Box>
                                    <Typography variant="body1" color="#0C141D">Microsoft</Typography>
                                </Box> */}
                            </Box>
                            <Box borderBottom="1px solid #D9D9D9" height="1px" position="relative" width="100%" margin="34px auto 28px"><Typography variant="body2" color="#667085" bgcolor="#ffffff" position="absolute" left="50%" sx={{ transform: "translate(-50%, -50%)" }} p="0 6px">Or</Typography></Box>
                            {/* -----------commented below code as per the requirement---------- */}
                        </Box>
                        <Grid container rowSpacing={{ xs: 1, sm: 1, md: 1 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }} mb="38px">
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    variant="outlined"
                                    label="Work email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    error={!!fieldErrors.email}
                                    helperText={fieldErrors.email}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: '3px',
                                            borderColor: '#666666',
                                        },

                                        " .MuiInputLabel-root.Mui-focused": {
                                            color: "inherit",
                                        },

                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: '#333333',
                                            border: '1px solid'
                                        },

                                        "& .MuiOutlinedInput-input": {
                                            padding: '14px 16px',
                                            background: '#ffffff',
                                            borderRadius: '8px'
                                        },
                                        "&.MuiInputBase-input::placeholder": {
                                            color: '#333333',
                                            fontSize: '16px',
                                            opacity: '0.8'
                                        },
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    variant="outlined"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    error={!!fieldErrors.password}
                                    helperText={fieldErrors.password}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: '3px',
                                            borderColor: '#666666',
                                            background: '#ffffff',
                                            paddingRight: '0'
                                        },

                                        " .MuiInputLabel-root.Mui-focused": {
                                            color: "inherit",
                                        },

                                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                            borderColor: '#333333',
                                            border: '1px solid'
                                        },

                                        "& .MuiOutlinedInput-input": {
                                            padding: '14px 16px',
                                            background: '#ffffff',
                                            borderRadius: '8px'
                                        },
                                        "&.MuiInputBase-input::placeholder": {
                                            color: '#333333',
                                            fontSize: '16px',
                                            opacity: '0.8'
                                        },
                                    }}
                                    slotProps={{
                                        input: {
                                            endAdornment: <InputAdornment position="start"><IconButton onClick={handleClickShowPassword}
                                                sx={{ borderRadius: "2px", "&:hover": { backgroundColor: "unset" } }}
                                                onMouseDown={handleMouseDownPassword}
                                                onMouseUp={handleMouseUpPassword}  >
                                                {!showPassword ? <Box display="flex" gap="4px" alignItems="center" color="#000000"><VisibilityIcon fontSize='small' />
                                                    <Typography component="span" color="#000000" fontSize="14px">Show</Typography></Box>
                                                    : <Box display="flex" gap="4px" alignItems="center" color="#000000">
                                                        <VisibilityOffIcon fontSize='small'></VisibilityOffIcon> <Typography component="span" fontSize="14px">hide</Typography></Box>
                                                }
                                            </IconButton>
                                            </InputAdornment>
                                        },
                                    }}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <Box marginTop="25px" mb="24px">
                            <Typography
                                variant="body2"
                                color="#060023"
                                component="a"
                                onClick={() => navigate('/forgot-password')}
                                sx={{
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                            >
                                Forgot your password?
                            </Typography>
                        </Box>
                        <form onSubmit={handleSubmit}>
                            <Box margin="auto">
                                <Button
                                    variant="contained"
                                    type="submit"
                                    disabled={isLoading}
                                    disableElevation
                                    sx={{
                                        display: "flex", alignItems: "center", justifyContent: "center", textTransform: "capitalize", width: "100%", height: "51px", gap: "8px", fontSize: "16px", fontWeight: "500", bgcolor: "#0382F7 !important",
                                        "&:hover": { bgcolor: "#006dd1 !important" }
                                    }}
                                >
                                    {isLoading ? 'Logging In...' : 'Log in'}
                                    {/* <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <g clipPath="url(#clip0_15809_100515)">
                                            <path d="M6.5 3.9375L11.5 8.9375L6.5 13.9375" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_15809_100515">
                                                <rect width="16" height="16" fill="white" transform="translate(0.5 0.9375)" />
                                            </clipPath>
                                        </defs>
                                    </svg> */}
                                </Button>
                            </Box>
                        </form>
                        {
                            value === 0 ? (
                                <Box className="login-authenticator-wrapper" sx={{ position: "unset!important" }}>
                                    <Box
                                        className="ew-copyright"
                                        sx={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: { xs: "20px", md: "unset" },
                                            justifyContent: { xs: "center" },
                                            alignItems: 'center',
                                            padding: '0 !important',
                                            position: 'unset!important',
                                            margin: '36px 0 0 !important',
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            sx={{ paddingRight: "13px !important", fontWeight: "500", color: "#667085 !important" }}
                                            borderRight={{ xs: "none !Important", sm: "1px solid #c5c5c5 !Important" }}
                                        >
                                            &copy; 2025 All Rights Reserved
                                        </Typography>
                                        <Box
                                            component="ul"
                                            sx={{
                                                display: 'flex',
                                                listStyle: 'none',
                                                margin: 0,
                                                padding: 0,
                                                gap: '4px',
                                                position: 'relative',
                                                flexWrap: 'wrap',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <Box
                                                component="li"
                                            >
                                                <Link
                                                    href="https://easywebinar.com/terms-conditions/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className='terms'
                                                    sx={{
                                                        textDecoration: 'none',
                                                        color: "#667085 !important",
                                                        fontSize: '14px',
                                                        fontWeight: "500",
                                                        marginRight: "4px",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },

                                                    }}
                                                >
                                                    Terms of Service
                                                </Link>
                                            </Box>
                                            <Box
                                                component="li"
                                            >
                                                <Link
                                                    href="https://easywebinar.com/privacy/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        color: "#667085 !important",
                                                        fontSize: '14px',
                                                        fontWeight: "500",
                                                        marginRight: "4px",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Privacy Policy
                                                </Link>
                                            </Box>
                                            <Box
                                                component="li"
                                            >
                                                <Link
                                                    href="https://easywebinar.com/cookies/"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        textDecoration: 'none',
                                                        fontWeight: "500",
                                                        color: "#667085 !important",
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Security
                                                </Link>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            ) : (
                                <>
                                </>
                            )
                        }
                    </Box>
                </Box >
            </Box >
            {/* ------------------------- signin component ------------------------ */}

            <AccountVerification
                open={verificationDialogOpen}
                onClose={handleVerificationDialogClose}
                onProceed={handleVerificationDialogProceed}
                phoneNumber=""
                email={formData.email}
                companyId={verificationData.companyId}
                companyName={verificationData.companyName}
                infLeadSquaredId={verificationData.infLeadSquaredId}
            />

            <ConfirmMfa
                open={confirmMfaDialogOpen}
                onClose={handleConfirmMfaDialogClose}
                onProceed={handleConfirmMfaDialogProceed}
                phoneNumber=""
                email={formData.email}
                challengeName={confirmMfaData.challengeName}
                session={confirmMfaData.session}
            />

        </>
    )
}
export default Login
